import React, { useState } from 'react';
import {
  IAuditoriaOutrasDespesasResultado,
  IAuditoriaOutrasDespesasItem,
} from '../interfaces/IAuditoriaOutrasDespesas';
import { formatDate } from '../utils/formatters';

interface Props {
  resultado: IAuditoriaOutrasDespesasResultado;
}

const formatCurrency = (value?: number) =>
  (value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatPct = (value?: number) =>
  `${(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const cls =
    status === 'ERRO'   ? 'badge-light-danger' :
    status === 'ALERTA' ? 'badge-light-warning' :
    status === 'AVISO'  ? 'badge-light-warning' :
                          'badge-light-success';
  return <span className={`badge ${cls}`}>{status}</span>;
};

// COR-290: drill-down a nível de lançamento — todo AVISO/ALERTA/ERRO precisa mostrar não só a
// concentração/variação agregada do mês, mas os lançamentos reais de Outras Despesas.
const LIMITE_LANCAMENTOS_VISIVEIS = 10;

const BlocoLancamentosRelacionados: React.FC<{ item: IAuditoriaOutrasDespesasItem }> = ({ item }) => {
  const [mostrarTodos, setMostrarTodos] = useState(false);
  const lancamentos = item.lancamentosRelacionados || [];

  if (lancamentos.length === 0) return null;

  const visiveis = mostrarTodos ? lancamentos : lancamentos.slice(0, LIMITE_LANCAMENTOS_VISIVEIS);

  return (
    <div className='mb-3'>
      <strong className='fs-8'>Lançamentos Relacionados ({lancamentos.length}):</strong>
      <div className='table-responsive mt-1'>
        <table className='table table-sm' style={{ fontSize: 12 }}>
          <thead>
            <tr>
              <th>Data</th>
              <th>Nº Lançamento</th>
              <th>Conta</th>
              <th>Histórico</th>
              <th className='text-end'>Valor</th>
            </tr>
          </thead>
          <tbody>
            {visiveis.map((lanc, idx) => (
              <tr key={`${lanc.numeroLancamento}-${lanc.data}-${idx}`}>
                <td>{formatDate(lanc.data)}</td>
                <td><code>{lanc.numeroLancamento || '-'}</code></td>
                <td><code>{lanc.contaContabil}</code></td>
                <td>{lanc.historico}</td>
                <td className='text-end'>{formatCurrency(lanc.valor)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {lancamentos.length > LIMITE_LANCAMENTOS_VISIVEIS && !mostrarTodos && (
        <button
          type='button'
          className='btn btn-link btn-sm p-0 mt-1'
          style={{ fontSize: 12 }}
          onClick={() => setMostrarTodos(true)}
        >
          + {lancamentos.length - LIMITE_LANCAMENTOS_VISIVEIS} lançamento(s) adicional(is)
        </button>
      )}
    </div>
  );
};

const ItemExpandivel: React.FC<{ item: IAuditoriaOutrasDespesasItem }> = ({ item }) => {
  const [expandido, setExpandido] = useState(false);

  return (
    <>
      <tr style={{ cursor: 'pointer' }} onClick={() => setExpandido(!expandido)}>
        <td>
          <span className='fw-bold'>{item.empresaNome}</span>
          <br />
          <span className='text-muted fs-8'>{item.cnpj}</span>
        </td>
        <td className='fw-bold'>{item.anoMes}</td>
        <td>{formatCurrency(item.outrasDespesas)}</td>
        <td>{formatCurrency(item.totalDespesas)}</td>
        <td className='fw-bold'>{formatPct(item.ratio)}</td>
        <td className='text-muted'>
          {item.temMediaMovel
            ? `${formatPct(item.mediaMovel6m)} (var: ${item.variacao >= 0 ? '+' : ''}${item.variacao.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}%)`
            : '—'}
        </td>
        <td><StatusBadge status={item.status} /></td>
        <td className='text-end'>{expandido ? '▲' : '▼'}</td>
      </tr>

      {expandido && (
        <tr>
          <td colSpan={8} className='bg-light-secondary p-4'>
            <div className='mb-3'>
              <strong>Diagnóstico:</strong>
              <p className='text-muted mt-1 mb-2'>{item.mensagemDiagnostico}</p>
            </div>

            <BlocoLancamentosRelacionados item={item} />

            {item.status === 'ERRO' && (
              <div className='row mb-4'>
                <div className='col-2 text-center border-end'>
                  <div className='fs-5 fw-bolder text-muted'>{formatCurrency(item.excessoDespesas)}</div>
                  <div className='text-muted fs-8'>Excesso em 3.01.01.08</div>
                </div>
                <div className='col-2 text-center border-end'>
                  <div className='fs-5 fw-bolder text-danger'>{formatCurrency(item.tributosIrpjCsll)}</div>
                  <div className='text-muted fs-8'>IRPJ/CSLL (34%)</div>
                </div>
                <div className='col-2 text-center border-end'>
                  <div className='fs-5 fw-bolder text-danger'>{formatCurrency(item.multaArt44)}</div>
                  <div className='text-muted fs-8'>Multa Art. 44 (75%)</div>
                </div>
                <div className='col-2 text-center border-end'>
                  <div className='fs-5 fw-bolder text-warning'>{formatCurrency(item.jurosSelic)}</div>
                  <div className='text-muted fs-8'>Juros SELIC</div>
                </div>
                <div className='col-4 text-center'>
                  <div className='fs-4 fw-bolder text-danger'>{formatCurrency(item.passivoTributario)}</div>
                  <div className='text-muted fs-8'>Passivo Total</div>
                </div>
              </div>
            )}

            {item.status === 'ERRO' && (
              <div className='alert alert-danger mt-3 py-2'>
                <strong>Regularização obrigatória:</strong> Concentração crítica na conta genérica 3.01.01.08 (&gt; 50%
                do total operacional ou crescimento &gt; 50% sobre µ6m). Reclassifique os lançamentos em contas analíticas
                e apresente documentação suporte (NF, contrato). Lei 6.404/76 Art. 177, Lei 9.430/96 Art. 44 I.
              </div>
            )}

            {item.status === 'ALERTA' && (
              <div className='alert alert-warning mt-3 py-2'>
                <strong>Ação recomendada:</strong> Concentração elevada em "Outras Despesas" (35–50% ou crescimento
                25–50% sobre µ6m). Reclassifique os lançamentos em contas analíticas adequadas.
                Resolução CFC nº 1.330/2011 (ITG 2000).
              </div>
            )}

            {item.status === 'AVISO' && (
              <div className='alert alert-warning mt-3 py-2'>
                <strong>Revisão sugerida:</strong> Crescimento moderado em "Outras Despesas" (20–35% ou crescimento
                10–25% sobre µ6m). Revise e classifique analiticamente os gastos em 3.01.01.08.
                Resolução CFC nº 1.330/2011 (ITG 2000).
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
};

const AuditoriaOutrasDespesasDetalhe: React.FC<Props> = ({ resultado }) => {
  const bannerCor =
    resultado.status === 'ERRO'   ? 'danger' :
    resultado.status === 'ALERTA' ? 'warning' :
    resultado.status === 'AVISO'  ? 'warning' :
                                    'success';

  return (
    <div className='card card-custom card-stretch'>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bolder fs-3 mb-1'>
            "Lixão Contábil" — Outras Despesas Operacionais — Detalhamento
          </span>
          <span className='text-muted mt-1 fw-bold fs-7'>
            Lei 6.404/76 Art. 177 · Resolução CFC 1.330/2011 (ITG 2000) · CPC 26 · Lei 9.430/96 Art. 44
          </span>
        </h3>
      </div>

      <div className='card-body pt-3'>
        {resultado.status !== 'CONCILIADO' && (
          <div className={`alert alert-${bannerCor} py-3 mb-5`}>
            <strong>{resultado.status}:</strong> {resultado.mensagemDirecionamento}
          </div>
        )}

        <div className='row mb-6'>
          <div className='col-3 text-center border-end'>
            <div className='fs-1 fw-bolder text-danger'>{resultado.totalErros}</div>
            <div className='text-muted'>Erros (&gt; 50%)</div>
            <div className='text-muted fs-8'>Concentração crítica ou crescimento &gt;50% µ6m</div>
          </div>
          <div className='col-3 text-center border-end'>
            <div className='fs-1 fw-bolder text-warning'>{resultado.totalAlertas}</div>
            <div className='text-muted'>Alertas (35–50%)</div>
            <div className='text-muted fs-8'>Concentração elevada ou crescimento 25–50% µ6m</div>
          </div>
          <div className='col-3 text-center border-end'>
            <div className='fs-1 fw-bolder text-warning'>{resultado.totalAvisos}</div>
            <div className='text-muted'>Avisos (20–35%)</div>
            <div className='text-muted fs-8'>Crescimento moderado 10–25% µ6m</div>
          </div>
          <div className='col-3 text-center'>
            <div className='fs-1 fw-bolder text-danger'>{formatCurrency(resultado.passivoTributarioTotal)}</div>
            <div className='text-muted'>Passivo Tributário Total</div>
            <div className='text-muted fs-8'>IRPJ/CSLL 34% + Multa 75% + SELIC</div>
          </div>
        </div>

        {resultado.itens.length === 0 ? (
          <div className='text-success fw-bold fs-5 text-center py-8'>
            ✓ Concentração de "Outras Despesas Operacionais" dentro dos limites aceitáveis. Situação regular.
          </div>
        ) : (
          <div className='table-responsive'>
            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
              <thead>
                <tr className='fw-bolder text-muted'>
                  <th>Empresa</th>
                  <th>Mês</th>
                  <th>Outras Desp.</th>
                  <th>Total Op.</th>
                  <th>Ratio</th>
                  <th>µ6m / Variação</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {resultado.itens.map((item, i) => (
                  <ItemExpandivel key={i} item={item} />
                ))}
              </tbody>
            </table>
          </div>
        )}

        <details className='mt-6'>
          <summary className='text-muted fw-bold cursor-pointer'>Base Legal</summary>
          <p className='text-muted mt-2 fs-7'>{resultado.fundamentacaoLegal}</p>
        </details>
      </div>
    </div>
  );
};

export default AuditoriaOutrasDespesasDetalhe;
